import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Lightning, ArrowRight, ArrowLeft, DeviceMobile } from "@phosphor-icons/react";
import api, { formatErr } from "@/lib/api";
import ThemeToggle from "@/components/layout/ThemeToggle";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [step, setStep] = useState("credentials"); // credentials | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [allowRegistration, setAllowRegistration] = useState(false);
  const [showDemoLogin, setShowDemoLogin] = useState(false);
  const [passwordResetEnabled, setPasswordResetEnabled] = useState(false);
  const [mfaAvailable, setMfaAvailable] = useState(false);

  const [mfaToken, setMfaToken] = useState("");
  const [phoneHint, setPhoneHint] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);

  useEffect(() => {
    if (location.state?.message) {
      setErr("");
    }
  }, [location.state]);

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setAllowRegistration(!!data.allow_registration);
      setShowDemoLogin(!!data.show_demo_login);
      setPasswordResetEnabled(!!data.password_reset_enabled);
      setMfaAvailable(!!data.mfa_enabled);
    }).catch(() => {});
  }, []);

  const startOtpFlow = (mfaData) => {
    setMfaToken(mfaData.mfa_token);
    setPhoneHint(mfaData.phone_hint || "");
    setStep("otp");
    setOtp("");
    setInfo("Verification code sent to your phone.");
    setErr("");
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    setErr("");
    setInfo("");
    setLoading(true);
    try {
      if (mode === "login") {
        const result = await login(email, password);
        if (result?.requiresMfa) {
          startOtpFlow(result);
          return;
        }
      } else {
        await api.post("/auth/register", { email, password, name });
        const result = await login(email, password);
        if (result?.requiresMfa) {
          startOtpFlow(result);
          return;
        }
      }
      navigate("/");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    setErr("");
    setLoading(true);
    try {
      if (!mfaToken) {
        throw new Error("Session expired — sign in again.");
      }
      await verifyMfa(mfaToken, otp);
      navigate("/");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!mfaToken) return;
    setErr("");
    setInfo("");
    setOtpSending(true);
    try {
      const { data } = await api.post("/auth/resend-mfa", { mfa_token: mfaToken });
      if (data.mfa_token) setMfaToken(data.mfa_token);
      setPhoneHint(data.phone_hint || phoneHint);
      setInfo("New code sent.");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setOtpSending(false);
    }
  };

  const backToCredentials = () => {
    setStep("credentials");
    setOtp("");
    setMfaToken("");
    setPhoneHint("");
    setInfo("");
    setErr("");
  };

  const isOtpStep = mode === "login" && step === "otp";

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-app-bg text-app-text overflow-x-hidden relative">
      <div className="absolute top-3 right-3 z-20">
        <ThemeToggle compact />
      </div>
      <div className="md:hidden relative px-4 py-8 border-b login-hero overflow-hidden">
        <img
          src="/login-bg.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b0c0f]/80 via-[#323d4b]/50 to-[#1448d7]/30 dark:from-[#0a0a0a]/60 dark:via-transparent dark:to-[#0a0a0a]" />
        <div className="relative z-10 text-center text-white">
          <Lightning size={28} weight="fill" className="text-app-accent mx-auto mb-3" />
          <h1 className="font-display font-black text-[clamp(1.75rem,6vw,2.5rem)] tracking-tight leading-tight mb-2">
            COMMAND YOUR FLEET
          </h1>
          <p className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-app-subtle">
            Real-time MT5 monitoring · Risk-controlled execution
          </p>
        </div>
      </div>

      <div className="hidden md:flex md:w-1/2 lg:w-[45%] relative items-end justify-start p-8 lg:p-12 login-hero border-r overflow-hidden shrink-0">
        <img
          src="/login-bg.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c0f]/92 via-[#323d4b]/35 to-transparent dark:from-[#0a0a0a] dark:via-transparent dark:to-transparent" />
        <div className="relative z-10 max-w-md text-white">
          <Lightning size={32} weight="fill" className="text-app-accent mb-6" />
          <h1 className="font-display font-black text-[clamp(2.5rem,5vw,3.5rem)] tracking-tight leading-none mb-4">
            COMMAND<br />YOUR FLEET.
          </h1>
          <p className="font-mono-d text-xs uppercase tracking-[0.25em] text-white/75 dark:text-app-subtle">
            Real-time MT5 monitoring · Risk-controlled execution
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 min-w-0 login-form-panel">
        {isOtpStep ? (
          <form onSubmit={submitOtp} className="w-full max-w-sm" data-testid="mfa-form">
            <button
              type="button"
              onClick={backToCredentials}
              className="mb-4 flex items-center gap-1 font-mono-d text-[10px] uppercase tracking-widest text-app-subtle hover:text-app-text"
            >
              <ArrowLeft size={12} /> Back
            </button>
            <div className="font-mono-d text-[10px] uppercase tracking-[0.3em] text-app-subtle mb-2 flex items-center gap-2">
              <DeviceMobile size={14} /> Two-factor auth
            </div>
            <h2 className="font-display font-black text-[clamp(1.5rem,5vw,1.875rem)] tracking-tight mb-2">
              Enter verification code
            </h2>
            <p className="text-sm text-app-subtle mb-6">
              {otpSending
                ? "Sending code…"
                : `We sent a 6-digit code to ${phoneHint || "your phone"}.`}
            </p>

            <div className="mb-6 flex justify-center">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="h-11 w-11 text-base" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            {info && (
              <div className="mb-4 px-3 py-2 bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] text-xs">
                {info}
              </div>
            )}

            {err && (
              <div className="mb-4 px-3 py-2 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF6B61] text-xs break-words" data-testid="auth-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || otp.length !== 6 || otpSending}
              data-testid="mfa-submit"
              className="btn-touch w-full bg-app-accent hover:opacity-90 disabled:opacity-50 text-white py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? "Verifying…" : "Verify & sign in"}
              <ArrowRight size={14} weight="bold" />
            </button>

            <button
              type="button"
              disabled={otpSending}
              onClick={resendOtp}
              className="btn-touch mt-4 w-full text-center font-mono-d text-[10px] uppercase tracking-[0.25em] text-app-subtle hover:text-app-text transition"
            >
              Resend code
            </button>
          </form>
        ) : (
          <form onSubmit={submitCredentials} className="w-full max-w-sm" data-testid="auth-form">
            <div className="font-mono-d text-[10px] uppercase tracking-[0.3em] text-app-subtle mb-2">
              {mode === "login" ? "Sign in" : "Create account"}
            </div>
            <h2 className="font-display font-black text-[clamp(1.5rem,5vw,1.875rem)] tracking-tight mb-6 sm:mb-8">
              {mode === "login" ? "Access terminal." : "Open access."}
            </h2>

            {mode === "register" && (
              <div className="mb-4">
                <label className="block font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-2">Name</label>
                <input
                  data-testid="name-input"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-app-surface border border-app-border px-3 py-3 text-sm focus:outline-none focus:border-app-accent min-h-[44px]"
                />
              </div>
            )}

            <div className="mb-4">
              <label className="block font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-2">Email</label>
              <input
                data-testid="email-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-app-surface border border-app-border px-3 py-3 text-sm focus:outline-none focus:border-app-accent min-h-[44px]"
              />
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">Password</label>
                {mode === "login" && passwordResetEnabled && (
                  <Link to="/forgot-password" className="font-mono-d text-[10px] uppercase tracking-widest text-app-accent hover:opacity-80">
                    Forgot?
                  </Link>
                )}
              </div>
              <input
                data-testid="password-input"
                type="password"
                required
                minLength={8}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-app-surface border border-app-border px-3 py-3 text-sm focus:outline-none focus:border-app-accent min-h-[44px]"
              />
            </div>

            {location.state?.message && (
              <div className="mb-4 px-3 py-2 bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] text-xs" data-testid="auth-success">
                {location.state.message}
              </div>
            )}

            {err && (
              <div className="mb-4 px-3 py-2 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF6B61] text-xs break-words" data-testid="auth-error">
                {err}
              </div>
            )}

            <button
              disabled={loading}
              data-testid="auth-submit"
              className="btn-touch w-full bg-app-accent hover:opacity-90 disabled:opacity-50 text-white py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? "Working..." : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight size={14} weight="bold" />
            </button>

            {allowRegistration && (
              <button
                type="button"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                data-testid="auth-mode-toggle"
                className="btn-touch mt-4 w-full text-center font-mono-d text-[10px] uppercase tracking-[0.25em] text-app-subtle hover:text-app-text transition"
              >
                {mode === "login" ? "› Register new account" : "‹ Back to sign in"}
              </button>
            )}

            {mode === "login" && showDemoLogin && (
              <div className="mt-6 sm:mt-8 pt-4 border-t border-app-border">
                <div className="font-mono-d text-[9px] uppercase tracking-[0.25em] text-app-muted mb-2">Demo accounts</div>
                <div className="font-mono-d text-[10px] text-app-subtle space-y-1 break-all">
                  <div>admin@mt5hub.com / admin123</div>
                  <div>manager@mt5hub.com / manager123</div>
                  <div>viewer@mt5hub.com / viewer123</div>
                </div>
              </div>
            )}

            {mode === "login" && mfaAvailable && (
              <p className="mt-4 font-mono-d text-[9px] uppercase tracking-[0.2em] text-app-muted text-center">
                MFA via SMS when enabled on your account
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
